import { useTranslation } from 'react-i18next';

/**
 * The name of an event, membership plan or benefit.
 *
 * These records carry three names: an internal one staff type in, and an
 * English and a Spanish one customers see. Several of the original grids showed
 * the internal name as the heading, so an admin comparing a screen against what
 * a customer sees had to translate between two names for the same thing.
 *
 * The customer-facing name in the current language leads; the internal name
 * sits above it, small and muted, because the slot *below* a name is already
 * taken on most of these rows by a location or a date.
 */

interface Named {
  nameEn?: string | null;
  nameEs?: string | null;
  internalName?: string | null;
  name?: string | null;
}

export function localizedName(
  record: Named | null | undefined,
  language: string,
  fallbackToInternal = true
): string {
  if (!record) return '';
  const localized = language === 'en' ? record.nameEn : record.nameEs;
  // The other language before the internal name: a plan with only a Spanish
  // name still reads better to an English admin as its Spanish name than as
  // "gold-tier-2024-v2".
  const value = localized || record.nameEn || record.nameEs;
  if (value) return value;
  return fallbackToInternal ? (record.internalName || record.name || '') : '';
}

export function LocalizedName({
  nameEn,
  nameEs,
  internalName,
}: {
  nameEn?: string | null;
  nameEs?: string | null;
  internalName?: string | null;
}) {
  const { i18n } = useTranslation();
  const primary = localizedName(
    { nameEn, nameEs, internalName },
    i18n.resolvedLanguage ?? 'en'
  );
  // Not repeated when it is already the heading.
  const showInternal = internalName && internalName !== primary;

  return (
    <div className="min-w-0">
      {showInternal && (
        <p className="text-muted-foreground truncate text-xs italic">
          {internalName}
        </p>
      )}
      <p className="font-medium break-words">{primary}</p>
    </div>
  );
}
